<template>
  <nav v-if="totalPages > 1" aria-label="Pagination">
    <ul class="pagination justify-content-center mb-0">
      <!-- First Page (hidden on mobile) -->
      <li
        class="page-item d-none d-md-inline-block"
        :class="{ disabled: currentPage === 1 }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="goToPage(1)"
          :aria-disabled="currentPage === 1"
        >
          <i class="fas fa-angle-double-left"></i>
        </a>
      </li>

      <!-- Previous Page -->
      <li class="page-item" :class="{ disabled: currentPage === 1 }">
        <a
          class="page-link"
          href="#"
          @click.prevent="goToPage(currentPage - 1)"
          :aria-disabled="currentPage === 1"
        >
          <i class="fas fa-angle-left"></i>
        </a>
      </li>

      <!-- Page Numbers -->
      <li
        v-for="page in visiblePages"
        :key="page"
        class="page-item"
        :class="{ active: page === currentPage, disabled: page === '...' }"
      >
        <a
          v-if="page !== '...'"
          class="page-link"
          href="#"
          @click.prevent="goToPage(page)"
        >
          {{ page }}
        </a>
        <span v-else class="page-link">...</span>
      </li>

      <!-- Next Page -->
      <li class="page-item" :class="{ disabled: currentPage === totalPages }">
        <a
          class="page-link"
          href="#"
          @click.prevent="goToPage(currentPage + 1)"
          :aria-disabled="currentPage === totalPages"
        >
          <i class="fas fa-angle-right"></i>
        </a>
      </li>

      <!-- Last Page (hidden on mobile) -->
      <li
        class="page-item d-none d-md-inline-block"
        :class="{ disabled: currentPage === totalPages }"
      >
        <a
          class="page-link"
          href="#"
          @click.prevent="goToPage(totalPages)"
          :aria-disabled="currentPage === totalPages"
        >
          <i class="fas fa-angle-double-right"></i>
        </a>
      </li>
    </ul>

    <!-- Page Info -->
    <div v-if="showInfo" class="text-center text-muted small mt-2">
      Page {{ currentPage }} of {{ totalPages }}
      <span v-if="total">({{ total }} total)</span>
    </div>
  </nav>
</template>

<script>
export default {
  name: "Pagination",
  props: {
    currentPage: {
      type: Number,
      required: true,
      default: 1,
    },
    totalPages: {
      type: Number,
      required: true,
      default: 1,
    },
    total: {
      type: Number,
      default: 0,
    },
    maxVisiblePages: {
      type: Number,
      default: 5,
    },
    maxVisiblePagesMobile: {
      type: Number,
      default: 3,
    },
    showInfo: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
    };
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    effectiveMaxVisiblePages() {
      return this.isMobile ? this.maxVisiblePagesMobile : this.maxVisiblePages;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const max = this.effectiveMaxVisiblePages;

      if (total <= max) {
        // Show all pages if total is less than max visible
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Always show exactly max + 2 elements: [1, a, b, c, ..., N]
        pages.push(1); // First page

        // Determine which pages to show in middle
        const half = Math.floor(max / 2);
        let centerStart = Math.max(2, current - half);
        let centerEnd = Math.min(total - 1, centerStart + max - 1);

        // Adjust if we hit the end
        if (centerEnd === total - 1) {
          centerStart = Math.max(2, total - max);
        }

        // Fill exactly `max` middle slots
        let middleSlot = 0;
        while (middleSlot < max) {
          const targetPage = centerStart + middleSlot;

          if (targetPage > 1 && targetPage < total) {
            // Check if we should show ellipsis or page number
            if (centerStart > 2 && middleSlot === 0) {
              pages.push("...");
            } else if (centerEnd < total - 1 && middleSlot === max - 1) {
              pages.push("...");
            } else {
              pages.push(targetPage);
            }
          } else {
            pages.push("...");
          }

          middleSlot++;
        }

        pages.push(total); // Last page
      }

      return pages;
    },
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit("page-changed", page);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.pagination {
  .page-item {
    margin: 0 0.15rem;

    .page-link {
      color: #333;
      border-color: #dee2e6;
      padding: 0.4rem 0.75rem;
      font-size: 0.875rem;
      border-radius: 0.5rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
      transition: all 0.2s ease;

      &:hover {
        background-color: #e9ecef;
        border-color: #dee2e6;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
        transform: translateY(-1px);
      }
    }

    &.active .page-link {
      background-color: #333;
      border-color: #333;
      color: #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &.disabled .page-link {
      color: #6c757d;
      pointer-events: none;
      background-color: #fff;
      border-color: #dee2e6;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
  }
}

// Mobile responsive styles
@media (max-width: 767px) {
  .pagination {
    .page-item {
      margin: 0 0.1rem;

      .page-link {
        padding: 0.4rem 0.6rem;
        font-size: 0.8rem;
        border-radius: 0.45rem;
        min-width: 36px;
        min-height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}
</style>
